// utils/calculateProjectHours.js

// HH:MM -> minutes
const parseMinutes = (str) => {
  if (!str || typeof str !== "string") return null;
  const [h, m] = str.split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

const rangesOverlap = (start1, end1, start2, end2) =>
  Math.max(start1, start2) < Math.min(end1, end2);

/**
 * Normalisera ett [from,to]-intervall till samma tidslinje som skiftet.
 * - Om intervallet korsar midnatt: to += 1440
 * - Om skiftet korsar midnatt (eMin > 1440) och intervallets slut fortfarande
 *   ligger "innan" skiftets start (t <= sMin), flytta intervallet till nästa dygn (+1440).
 */
const normalizeForShift = (fromMin, toMin, sMin, eMin) => {
  let f = fromMin;
  let t = toMin;
  if (t <= f) t += 1440;
  if (eMin > 1440 && t <= sMin) {
    f += 1440;
    t += 1440;
  }
  return [f, t];
};

/** Klipp ett intervall [a,b] mot [lo,hi] och returnera överlappslängd (>=0). */
const overlappedMinutes = (a, b, lo, hi) =>
  Math.max(0, Math.min(b, hi) - Math.max(a, b));

const calculateProjectHours = ({ projectId, shifts, absenceRules = [], overtimeRules = [] }) => {
  let totalWorkedMinutes = 0;
  let totalOvertimeMinutes = 0;
  let totalAbsenceMinutes = 0;
  let totalBreakMinutes = 0;
  let totalShiftCount = 0;
  const employeeIds = new Set();

  const relevantShifts = (shifts || []).filter(
    (shift) => shift.project === projectId
  );

  for (const shift of relevantShifts) {
    const sMinRaw = parseMinutes(shift.startTime);
    const eMinRaw = parseMinutes(shift.endTime);
    if (sMinRaw == null || eMinRaw == null) continue;

    const sMin = sMinRaw;
    let eMin = eMinRaw;
    if (eMin <= sMin) eMin += 1440; // skift över midnatt

    for (const emp of shift.employees || []) {
      // ⬅️ IMPORTANT FIX: skip rows without real employeeId
      if (!emp || !emp.employeeId) continue;

      employeeIds.add(emp.employeeId);
      totalShiftCount++;

      const grossMinutes = eMin - sMin;

      // ─────────────────────────────────────────────────────────────
      // 1) Normalisera och klipp frånvaro & raster till skiftet
      // ─────────────────────────────────────────────────────────────
      const normAbsences = Array.isArray(emp.absences)
        ? emp.absences
            .filter((a) => a && a.absenceType)
            .map((a) => {
              if (a.appliesWholeShift) {
                return { from: sMin, to: eMin };
              }
              const af = parseMinutes(a.from);
              const at = parseMinutes(a.to);
              if (af == null || at == null) return null;

              let [naFrom, naTo] = normalizeForShift(af, at, sMin, eMin);
              naFrom = Math.max(naFrom, sMin);
              naTo = Math.min(naTo, eMin);

              if (naTo <= naFrom) return null;
              return { from: naFrom, to: naTo };
            })
            .filter(Boolean)
        : [];

      const normBreaks = Array.isArray(emp.breaks)
        ? emp.breaks
            .filter((b) => b && b.from && b.to)
            .map((b) => {
              const bf = parseMinutes(b.from);
              const bt = parseMinutes(b.to);
              if (bf == null || bt == null) return null;

              let [nbFrom, nbTo] = normalizeForShift(bf, bt, sMin, eMin);
              nbFrom = Math.max(nbFrom, sMin);
              nbTo = Math.min(nbTo, eMin);

              if (nbTo <= nbFrom) return null;
              return { from: nbFrom, to: nbTo };
            })
            .filter(Boolean)
        : [];

      // ─────────────────────────────────────────────────────────────
      // 2) Rasterna (exkludera delar som överlappar frånvaro)
      // ─────────────────────────────────────────────────────────────
      let breakMinutes = 0;
      for (const brk of normBreaks) {
        let remaining = [[brk.from, brk.to]];
        for (const abs of normAbsences) {
          const next = [];
          for (const [r1, r2] of remaining) {
            if (!rangesOverlap(r1, r2, abs.from, abs.to)) {
              next.push([r1, r2]);
            } else {
              if (r1 < abs.from) next.push([r1, Math.max(r1, Math.min(r2, abs.from))]);
              if (abs.to < r2) next.push([Math.max(abs.to, r1), r2]);
            }
          }
          remaining = next.filter(([x1, x2]) => x2 > x1);
          if (!remaining.length) break;
        }
        for (const [r1, r2] of remaining) breakMinutes += r2 - r1;
      }
      totalBreakMinutes += breakMinutes;

      // ─────────────────────────────────────────────────────────────
      // 3) Frånvarominuter (skift ∩ frånvaro)
      // ─────────────────────────────────────────────────────────────
      let absenceMinutes = 0;
      for (const abs of normAbsences) {
        absenceMinutes += abs.to - abs.from;
      }
      totalAbsenceMinutes += absenceMinutes;

      // ─────────────────────────────────────────────────────────────
      // 4) Övertid: dela upp inside/outside
      // ─────────────────────────────────────────────────────────────
      let insideShiftMinutes = 0;
      let outsideShiftMinutes = 0;

      if (Array.isArray(emp.overtime)) {
        for (const ot of emp.overtime) {
          if (!ot || !ot.from || !ot.to) continue;

          let of = parseMinutes(ot.from);
          let otm = parseMinutes(ot.to);
          if (of == null || otm == null) continue;

          let [oFrom, oTo] = normalizeForShift(of, otm, sMin, eMin);

          const overlapsAbsence = normAbsences.some(a =>
            rangesOverlap(oFrom, oTo, a.from, a.to)
          );
          if (overlapsAbsence) continue;

          for (let m = oFrom; m < oTo; m++) {
            const inBreak = normBreaks.some((b) => m >= b.from && m < b.to);
            if (inBreak) continue;

            const inAbs = normAbsences.some((a) => m >= a.from && m < a.to);
            if (inAbs) continue;

            if (m >= sMin && m < eMin) insideShiftMinutes++;
            else outsideShiftMinutes++;
          }
        }
      }

      const shiftOvertimeMinutes = insideShiftMinutes + outsideShiftMinutes;

      const effectiveMinutes =
        grossMinutes -
        breakMinutes -
        absenceMinutes -
        insideShiftMinutes;

      totalOvertimeMinutes += shiftOvertimeMinutes;
      totalWorkedMinutes += Math.max(effectiveMinutes, 0);
    }
  }

  return {
    totalWorkedMinutes,
    totalOvertimeMinutes,
    totalAbsenceMinutes,
    totalBreakMinutes,
    totalShiftCount,
    employeeCount: employeeIds.size,
    totalCombinedMinutes: totalWorkedMinutes + totalOvertimeMinutes,
  };
};

module.exports = { calculateProjectHours };
